import { useEffect, useState } from 'react';
import { Download, Trash2, Upload, FileText } from 'lucide-react';
import { policyDocumentsAPI } from '../../services/api';

const TYPE_LABELS = {
  policy_schedule: 'Policy Schedule',
  benefit_grid: 'Benefit Grid',
  other: 'Other',
};

export default function PolicyDocumentsSection({ policyId, canManage = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState('policy_schedule');
  const [message, setMessage] = useState(null);

  const load = () => {
    setLoading(true);
    policyDocumentsAPI.list(policyId)
      .then(({ data }) => setDocs(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [policyId]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setMessage(null);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', documentType);
    try {
      await policyDocumentsAPI.upload(policyId, formData);
      setFile(null);
      setMessage('Document uploaded successfully.');
      load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    const { data } = await policyDocumentsAPI.download(policyId, doc.id);
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRemove = async (docId) => {
    if (!confirm('Remove this document from employee download?')) return;
    await policyDocumentsAPI.remove(policyId, docId);
    load();
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        Policy Documents
      </h2>
      <p className="text-sm text-slate-500 mb-4">Employees can download active documents from My Coverage.</p>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${message.includes('success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {canManage && (
        <form onSubmit={handleUpload} className="mb-6 p-4 bg-slate-50 rounded-lg grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="label">Document type</label>
            <select className="input-field" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">File (PDF / JPG / PNG)</label>
            <input
              type="file"
              className="input-field"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={!file || uploading}>
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-slate-500 text-sm">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="text-slate-500 text-sm">No policy documents uploaded yet.</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg text-sm">
              <div>
                <span className="font-medium">{doc.file_name}</span>
                <span className="text-slate-400 ml-2">({TYPE_LABELS[doc.document_type] || doc.document_type})</span>
                <div className="text-xs text-slate-400">{new Date(doc.created_at).toLocaleString('en-IN')}</div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => handleDownload(doc)} className="text-primary-600 hover:underline flex items-center gap-1">
                  <Download className="w-4 h-4" />
                  Download
                </button>
                {canManage && (
                  <button type="button" onClick={() => handleRemove(doc.id)} className="text-red-600 hover:underline flex items-center gap-1">
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
